import { useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Alert, Button, Container, Link as MuiLink, Paper, TextField, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { requestClientPasswordLink } from "../../../store/thunks/clientAuthThunks";

const schema = z.object({
  rfc: z.string().trim().min(12, "RFC debe tener 12 o 13 caracteres").max(13, "RFC debe tener 12 o 13 caracteres"),
});

type FormData = z.infer<typeof schema>;

/**
 * «¿Olvidaste tu contraseña?» — also the way in for an account staff created without one:
 * a link goes to the email the account has on file (GH126).
 */
export const ForgotPasswordPage = () => {
  const [sentMessage, setSentMessage] = useState<string>();
  const [error, setError] = useState<string>();

  const { control, handleSubmit, formState: { errors, isSubmitting } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { rfc: "" },
  });

  const onSubmit = async ({ rfc }: FormData) => {
    setError(undefined);
    try {
      setSentMessage(await requestClientPasswordLink(rfc));
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo enviar el enlace. Intenta de nuevo.");
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h5" align="center" gutterBottom fontWeight="bold" sx={{ color: "#0e368d" }}>
          Crear o recuperar tu contraseña
        </Typography>

        {sentMessage ? (
          <>
            <Alert severity="success" sx={{ my: 2 }}>{sentMessage}</Alert>
            <Typography variant="body2" align="center">
              <MuiLink component={Link} to="/facturacion/login">Volver a iniciar sesión</MuiLink>
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="body2" align="center" color="textSecondary" sx={{ mb: 3 }}>
              Escribe tu RFC y te mandamos un enlace al correo que tenemos guardado para que elijas tu contraseña.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="rfc"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="RFC"
                    fullWidth
                    margin="normal"
                    error={!!errors.rfc}
                    helperText={errors.rfc?.message}
                    inputProps={{ style: { textTransform: "uppercase" } }}
                    autoFocus
                  />
                )}
              />
              <Button
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                disabled={isSubmitting}
                sx={{ mt: 2, mb: 2, backgroundColor: "#0e368d", "&:hover": { backgroundColor: "#0b2b71" } }}
              >
                {isSubmitting ? "Enviando..." : "Enviarme el enlace"}
              </Button>
            </form>

            <Typography variant="body2" align="center">
              <MuiLink component={Link} to="/facturacion/login">Volver a iniciar sesión</MuiLink>
            </Typography>
          </>
        )}
      </Paper>
    </Container>
  );
};
